import { useCallback, useMemo, useState } from 'react';

import { useRecoilValue } from 'recoil';

import {
  giftCouponSearchDataAtomFamily,
  giftCouponSearchStatusAtomFamily,
  giftCouponStatisticsDataAtom,
} from '@shared/api/giftCoupon/giftCoupon.atom';
import {
  cancelIssueCoupon,
  deleteAdditionalCoupon,
  issueCoupon,
  resetCouponToNotShared,
  updateCouponDeliveredAt,
  updateCouponDeliveryChannel,
  updateCouponPhoneAndMemo,
} from '@shared/api/giftCoupon/giftCoupon.controller';
import {
  useGiftCouponSearchHook,
  useGiftCouponStatisticsHook,
} from '@shared/api/giftCoupon/giftCoupon.hook';
import {
  DateField,
  DeliveryChannel,
  GiftCouponForRedemption,
  GiftCouponRedeemStatus,
  GiftCouponSearchParams,
  GiftCouponShareStatus,
  formatDeliveryChannelSelectLabel,
} from '@shared/api/giftCoupon/giftCoupon.interface';
import { useQueryClient } from 'react-query';

import { formatDateTimeSec } from '../utils/couponSearch.utils';
import { useToast } from './useToast';

type SortDirection = 'asc' | 'desc';

export type CouponHistorySearchHook = ReturnType<typeof useCouponHistorySearch>;

export function useCouponHistorySearch() {
  const queryClient = useQueryClient();
  useGiftCouponStatisticsHook();
  const statistics = useRecoilValue(giftCouponStatisticsDataAtom);

  // 토스트 — 공용 훅 사용 (1200ms)
  const { message: toastMessage, show: showToast, durationMs: toastDurationMs } =
    useToast(1200);
  const copyToClipboard = useCallback(
    (text: string) => {
      navigator.clipboard.writeText(text);
      showToast('복사되었습니다.');
    },
    [showToast],
  );

  // 검색 입력 상태
  const [phoneNumber, setPhoneNumber] = useState('');
  const [ordererName, setOrdererName] = useState('');
  const [ordererPhone, setOrdererPhone] = useState('');
  const [fullCode, setFullCode] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [dateField, setDateField] = useState<DateField>('requestedAt');
  const [redeemStatus, setRedeemStatus] = useState<GiftCouponRedeemStatus[]>([]);
  const [shareStatus, setShareStatus] = useState<string>('');

  // 정렬
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const handleSort = useCallback(() => {
    setSortDirection((d) => (d === 'desc' ? 'asc' : 'desc'));
  }, []);

  // 검색 실행 상태
  const [searchKey, setSearchKey] = useState('');
  const [searchParams, setSearchParams] = useState<GiftCouponSearchParams | null>(null);
  const [hasSearched, setHasSearched] = useState(false);

  useGiftCouponSearchHook(searchKey, searchParams || {}, !searchKey || !searchParams);

  const status = useRecoilValue(giftCouponSearchStatusAtomFamily(searchKey));
  const searchResults = useRecoilValue(giftCouponSearchDataAtomFamily(searchKey));

  const [showBlacklist, setShowBlacklist] = useState(false);

  const handleSearch = useCallback(() => {
    if (
      !phoneNumber &&
      !ordererName &&
      !ordererPhone &&
      !fullCode &&
      !dateFrom &&
      !dateTo &&
      redeemStatus.length === 0 &&
      !shareStatus
    ) {
      alert('검색 조건을 하나 이상 입력해주세요.');
      return;
    }

    const params: GiftCouponSearchParams = {};
    if (phoneNumber) params.phoneNumber = phoneNumber.replace(/[^0-9]/g, '');
    if (ordererName) params.ordererName = ordererName;
    if (ordererPhone) params.ordererPhone = ordererPhone.replace(/[^0-9]/g, '');
    if (fullCode) params.fullCode = fullCode.trim();
    if (dateFrom) params.dateFrom = dateFrom;
    if (dateTo) params.dateTo = dateTo;
    if (dateFrom || dateTo) params.dateField = dateField;
    if (redeemStatus.length > 0) params.redeemStatus = redeemStatus;
    if (shareStatus) params.shareStatus = shareStatus as GiftCouponShareStatus;

    const key = JSON.stringify(params);
    setSearchKey(key);
    setSearchParams(params);
    setHasSearched(true);
  }, [
    phoneNumber,
    ordererName,
    ordererPhone,
    fullCode,
    dateFrom,
    dateTo,
    dateField,
    redeemStatus,
    shareStatus,
  ]);

  const searchByStatus = useCallback((status: GiftCouponRedeemStatus) => {
    setPhoneNumber('');
    setOrdererName('');
    setOrdererPhone('');
    setFullCode('');
    setDateFrom('');
    setDateTo('');
    setDateField('requestedAt');
    setShareStatus('');
    setRedeemStatus([status]);
    setTimeout(() => {
      const params: GiftCouponSearchParams = { redeemStatus: [status] };
      const key = JSON.stringify(params);
      setSearchKey(key);
      setSearchParams(params);
      setHasSearched(true);
    }, 0);
  }, []);

  const searchByPhone = useCallback((phone: string) => {
    setPhoneNumber(phone);
    setRedeemStatus([]);
    setShareStatus('');
    setOrdererName('');
    setOrdererPhone('');
    setFullCode('');
    setDateFrom('');
    setDateTo('');
    setTimeout(() => {
      const params: GiftCouponSearchParams = {
        phoneNumber: phone.replace(/[^0-9]/g, ''),
      };
      const key = JSON.stringify(params);
      setSearchKey(key);
      setSearchParams(params);
      setHasSearched(true);
    }, 0);
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') handleSearch();
    },
    [handleSearch],
  );

  // 발송매체 편집 모드
  const [channelEditMode, setChannelEditMode] = useState(false);

  const results = useMemo(() => {
    if (!searchResults) return [];
    return [...searchResults].sort((a, b) => {
      if (channelEditMode) {
        const aTime = a.gifticon?.deliveredAt
          ? new Date(a.gifticon.deliveredAt).getTime()
          : 0;
        const bTime = b.gifticon?.deliveredAt
          ? new Date(b.gifticon.deliveredAt).getTime()
          : 0;
        return bTime - aTime;
      }
      const aTime = a.recipient?.requestedAt
        ? new Date(a.recipient.requestedAt).getTime()
        : 0;
      const bTime = b.recipient?.requestedAt
        ? new Date(b.recipient.requestedAt).getTime()
        : 0;
      return sortDirection === 'desc' ? bTime - aTime : aTime - bTime;
    });
  }, [searchResults, sortDirection, channelEditMode]);

  // 선택 (미전달 처리용)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isUnsharing, setIsUnsharing] = useState(false);

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    const selectable = results.filter(
      (c) => c.redeemStatus !== GiftCouponRedeemStatus.Delivered,
    );
    setSelectedIds((prev) => {
      if (selectable.length > 0 && selectable.every((c) => prev.has(c._id))) {
        return new Set();
      }
      return new Set(selectable.map((c) => c._id));
    });
  }, [results]);

  const selectedShareableCount = useMemo(() => {
    return results.filter(
      (c) =>
        selectedIds.has(c._id) && c.redeemStatus !== GiftCouponRedeemStatus.Delivered,
    ).length;
  }, [results, selectedIds]);

  const handleUnshare = useCallback(async () => {
    const targets = results.filter(
      (c) =>
        selectedIds.has(c._id) && c.redeemStatus !== GiftCouponRedeemStatus.Delivered,
    );
    if (targets.length === 0) {
      alert('미전달 상태로 변경할 쿠폰을 선택해주세요.');
      return;
    }
    if (
      !confirm(
        `선택한 ${targets.length}건의 쿠폰을 미전달(NotShared) + 교환가능(Available) 상태로 변경하시겠습니까?`,
      )
    )
      return;

    setIsUnsharing(true);
    try {
      await Promise.all(targets.map((c) => resetCouponToNotShared(c._id)));
      showToast(`${targets.length}건 미전달 처리 완료`);
      setSelectedIds(new Set());
      await queryClient.refetchQueries({
        predicate: (query) => String(query.queryKey).includes('/gift-coupon/'),
      });
    } catch (err) {
      alert('미전달 처리 중 오류가 발생했습니다.');
      console.error(err);
    } finally {
      setIsUnsharing(false);
    }
  }, [results, selectedIds, showToast, queryClient]);

  // 공유상태 셀 클릭 → 단건 미전달(NotShared) + 교환가능(Available) 되돌리기.
  const handleResetShareStatusOne = useCallback(
    async (couponId: string) => {
      if (
        !confirm(
          '이 쿠폰을 미전달(NotShared) + 교환가능(Available) 상태로 변경하시겠습니까?',
        )
      )
        return;
      try {
        await resetCouponToNotShared(couponId);
        showToast('미전달 처리 완료');
        await queryClient.refetchQueries({
          predicate: (query) => String(query.queryKey).includes('/gift-coupon/'),
        });
      } catch (err) {
        alert('미전달 처리 중 오류가 발생했습니다.');
        console.error(err);
      }
    },
    [showToast, queryClient],
  );

  // 메모/확인번호 편집
  const [editingCouponId, setEditingCouponId] = useState<string | null>(null);
  const [editPhone, setEditPhone] = useState('');
  const [editMemo, setEditMemo] = useState('');
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  const startEditPhoneMemo = useCallback((coupon: GiftCouponForRedemption) => {
    setEditingCouponId(coupon._id);
    setEditPhone(coupon.additionalRecipientPhone?.replace(/[^0-9]/g, '') || '');
    setEditMemo(coupon.memo || '');
  }, []);

  const cancelEditPhoneMemo = useCallback(() => {
    setEditingCouponId(null);
    setEditPhone('');
    setEditMemo('');
  }, []);

  const saveEditPhoneMemo = useCallback(async () => {
    if (!editingCouponId) return;
    setIsSavingEdit(true);
    try {
      await updateCouponPhoneAndMemo(editingCouponId, {
        additionalRecipientPhone: editPhone || '',
        memo: editMemo || '',
      });
      showToast('저장되었습니다.');
      setEditingCouponId(null);
      await queryClient.refetchQueries({
        predicate: (query) => String(query.queryKey).includes('/gift-coupon/'),
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : '저장 중 오류가 발생했습니다.';
      alert(message);
      console.error(err);
    } finally {
      setIsSavingEdit(false);
    }
  }, [editingCouponId, editPhone, editMemo, showToast, queryClient]);

  // 발송일시 편집
  const [editingDeliveredAtId, setEditingDeliveredAtId] = useState<string | null>(null);
  const [editDeliveredAt, setEditDeliveredAt] = useState('');
  const [isSavingDeliveredAt, setIsSavingDeliveredAt] = useState(false);

  const toKstDateTimeLocal = useCallback((isoString: string | Date | undefined) => {
    if (!isoString) return '';
    const d = new Date(isoString);
    if (isNaN(d.getTime())) return '';
    const kst = new Date(d.getTime() + 9 * 60 * 60 * 1000);
    return kst.toISOString().slice(0, 16);
  }, []);

  const startEditDeliveredAt = useCallback(
    (coupon: GiftCouponForRedemption) => {
      setEditingDeliveredAtId(coupon._id);
      setEditDeliveredAt(toKstDateTimeLocal(coupon.gifticon?.deliveredAt));
    },
    [toKstDateTimeLocal],
  );

  const cancelEditDeliveredAt = useCallback(() => {
    setEditingDeliveredAtId(null);
    setEditDeliveredAt('');
  }, []);

  const saveEditDeliveredAt = useCallback(async () => {
    if (!editingDeliveredAtId || !editDeliveredAt) return;
    setIsSavingDeliveredAt(true);
    try {
      const isoValue = new Date(`${editDeliveredAt}:00+09:00`).toISOString();
      await updateCouponDeliveredAt(editingDeliveredAtId, isoValue);
      showToast('발송일시가 변경되었습니다.');
      setEditingDeliveredAtId(null);
      setEditDeliveredAt('');
      await queryClient.refetchQueries({
        predicate: (query) => String(query.queryKey).includes('/gift-coupon/'),
      });
    } catch (err) {
      const message =
        err instanceof Error ? err.message : '발송일시 변경 중 오류가 발생했습니다.';
      alert(message);
      console.error(err);
    } finally {
      setIsSavingDeliveredAt(false);
    }
  }, [editingDeliveredAtId, editDeliveredAt, showToast, queryClient]);

  // 발송매체 일괄변경
  const [channelSelectedIds, setChannelSelectedIds] = useState<Set<string>>(new Set());
  const [bulkChannelValue, setBulkChannelValue] = useState<DeliveryChannel | ''>('');
  const [isSavingChannel, setIsSavingChannel] = useState(false);

  const deliveredGroupMap = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const coupon of results) {
      if (coupon.redeemStatus !== GiftCouponRedeemStatus.Delivered) continue;
      const key = coupon.gifticon?.deliveredAt
        ? formatDateTimeSec(coupon.gifticon.deliveredAt)
        : '__none__';
      const ids = map.get(key);
      if (ids) ids.push(coupon._id);
      else map.set(key, [coupon._id]);
    }
    return map;
  }, [results]);

  const toggleChannelOne = useCallback((id: string) => {
    setChannelSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleChannelGroup = useCallback(
    (groupKey: string) => {
      const ids = deliveredGroupMap.get(groupKey);
      if (!ids) return;
      setChannelSelectedIds((prev) => {
        const next = new Set(prev);
        const allSelected = ids.every((id) => next.has(id));
        if (allSelected) ids.forEach((id) => next.delete(id));
        else ids.forEach((id) => next.add(id));
        return next;
      });
    },
    [deliveredGroupMap],
  );

  const handleBulkChannelApply = useCallback(async () => {
    const ids = Array.from(channelSelectedIds);
    if (ids.length === 0) return;

    const channelLabel = bulkChannelValue
      ? formatDeliveryChannelSelectLabel(bulkChannelValue)
      : '입력안함';
    if (
      !confirm(`${ids.length}건의 발송매체를 "${channelLabel}"(으)로 변경하시겠습니까?`)
    )
      return;

    setIsSavingChannel(true);
    let successCount = 0;
    let failCount = 0;
    for (const id of ids) {
      try {
        await updateCouponDeliveryChannel(id, bulkChannelValue);
        successCount++;
      } catch (err) {
        console.error(`발송매체 변경 실패 (${id}):`, err);
        failCount++;
      }
    }
    setIsSavingChannel(false);
    setChannelSelectedIds(new Set());

    await queryClient.refetchQueries({
      predicate: (query) => String(query.queryKey).includes('/gift-coupon/'),
    });

    if (failCount === 0) {
      showToast(`${successCount}건 발송매체 변경 완료`);
    } else {
      alert(`성공: ${successCount}건, 실패: ${failCount}건`);
    }
  }, [channelSelectedIds, bulkChannelValue, showToast, queryClient]);

  const toggleChannelEditMode = useCallback(() => {
    setChannelEditMode((prev) => {
      if (prev) setChannelSelectedIds(new Set());
      return !prev;
    });
  }, []);

  // 재발행 폐기
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const handleDeleteAdditional = useCallback(
    async (coupon: GiftCouponForRedemption) => {
      if (
        !confirm(
          `재발행 쿠폰 [${
            coupon.fullCode || coupon.code
          }]을(를) 폐기하시겠습니까?\nDB에서 완전 삭제되며 복구할 수 없습니다.`,
        )
      )
        return;

      setDeletingId(coupon._id);
      try {
        await deleteAdditionalCoupon(coupon._id);
        showToast('재발행 쿠폰이 폐기되었습니다.');
        await queryClient.refetchQueries({
          predicate: (query) => String(query.queryKey).includes('/gift-coupon/'),
        });
      } catch (err) {
        const message =
          err instanceof Error ? err.message : '폐기 처리 중 오류가 발생했습니다.';
        alert(message);
        console.error(err);
      } finally {
        setDeletingId(null);
      }
    },
    [showToast, queryClient],
  );

  // Issue (환불/연장 링크 발행)
  const [isIssuing, setIsIssuing] = useState(false);

  const handleIssueRefund = useCallback(
    async (couponId: string) => {
      setIsIssuing(true);
      try {
        await issueCoupon({ couponId, type: 'refund' });
        const refundUrl = `${window.location.origin}/gift-store/coupon/${couponId}/refund`;
        await navigator.clipboard.writeText(refundUrl);
        showToast('환불 링크가 발행되고 클립보드에 복사되었습니다.');
        await queryClient.refetchQueries({
          predicate: (query) => String(query.queryKey).includes('/gift-coupon/'),
        });
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Issue 발행 중 오류가 발생했습니다.';
        alert(message);
        console.error(err);
      } finally {
        setIsIssuing(false);
      }
    },
    [showToast, queryClient],
  );

  const handleIssueRenewal = useCallback(
    async (couponId: string) => {
      setIsIssuing(true);
      try {
        await issueCoupon({ couponId, type: 'renewal' });
        const renewalUrl = `${window.location.origin}/gift-store/coupon/${couponId}/renewal`;
        await navigator.clipboard.writeText(renewalUrl);
        showToast('연장 링크가 발행되고 클립보드에 복사되었습니다.');
        await queryClient.refetchQueries({
          predicate: (query) => String(query.queryKey).includes('/gift-coupon/'),
        });
      } catch (err) {
        const message =
          err instanceof Error ? err.message : '연장 링크 발행 중 오류가 발생했습니다.';
        alert(message);
        console.error(err);
      } finally {
        setIsIssuing(false);
      }
    },
    [showToast, queryClient],
  );

  const handleCancelIssue = useCallback(
    async (couponId: string) => {
      if (!confirm('발행된 링크를 취소하시겠습니까?')) return;
      setIsIssuing(true);
      try {
        await cancelIssueCoupon(couponId);
        showToast('링크 발행이 취소되었습니다.');
        await queryClient.refetchQueries({
          predicate: (query) => String(query.queryKey).includes('/gift-coupon/'),
        });
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Issue 취소 중 오류가 발생했습니다.';
        alert(message);
        console.error(err);
      } finally {
        setIsIssuing(false);
      }
    },
    [showToast, queryClient],
  );

  return {
    // 통계
    statistics,
    showBlacklist,
    setShowBlacklist,

    // 토스트
    toastMessage,
    showToast,
    toastDurationMs,
    copyToClipboard,

    // 검색 입력
    phoneNumber,
    setPhoneNumber,
    ordererName,
    setOrdererName,
    ordererPhone,
    setOrdererPhone,
    fullCode,
    setFullCode,
    dateFrom,
    setDateFrom,
    dateTo,
    setDateTo,
    dateField,
    setDateField,
    redeemStatus,
    setRedeemStatus,
    shareStatus,
    setShareStatus,

    // 정렬
    sortDirection,
    handleSort,

    // 검색 실행
    hasSearched,
    status,
    results,
    handleSearch,
    searchByStatus,
    searchByPhone,
    handleKeyDown,

    // 선택 / 미전달 처리
    selectedIds,
    toggleSelect,
    toggleSelectAll,
    selectedShareableCount,
    isUnsharing,
    handleUnshare,
    handleResetShareStatusOne,

    // 메모 편집
    editingCouponId,
    editPhone,
    setEditPhone,
    editMemo,
    setEditMemo,
    isSavingEdit,
    startEditPhoneMemo,
    cancelEditPhoneMemo,
    saveEditPhoneMemo,

    // 발송일시 편집
    editingDeliveredAtId,
    editDeliveredAt,
    setEditDeliveredAt,
    isSavingDeliveredAt,
    startEditDeliveredAt,
    cancelEditDeliveredAt,
    saveEditDeliveredAt,

    // 발송매체
    channelEditMode,
    toggleChannelEditMode,
    channelSelectedIds,
    setChannelSelectedIds,
    bulkChannelValue,
    setBulkChannelValue,
    isSavingChannel,
    deliveredGroupMap,
    toggleChannelOne,
    toggleChannelGroup,
    handleBulkChannelApply,

    // 재발행 폐기
    deletingId,
    handleDeleteAdditional,

    // issue (환불/연장 링크)
    isIssuing,
    handleIssueRefund,
    handleIssueRenewal,
    handleCancelIssue,
  };
}
